import React, { useState, useContext } from "react";
import { Col, Row, Button, Alert, Input } from "reactstrap";
import ToolkitProvider, {CSVExport} from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import ReleaseDropdown from "./releaseDropdown";
import ReleaseStoreUrls from "./releaseStoreUrls";
import QCNote from "./qcNote";
import ACRAlert from "./acrAlert";
import ViewLayout from "../components/viewLayout";
import FlaggedContent from "./flaggedContent";
import SubUserFilter from "./subUserFilter";
import { useTranslation } from "react-i18next";
import { StateContext } from "../utils/context";
import PreScan from "./preScan";

const qcNoteFormatter = (cell, row) => {
  return (
    <QCNote {...row} noteType={"Release"}></QCNote>
  );
};


const ReleaseTable = ({
  tableData,
  defaultSorted,
  forceUpdate,
  setForceUpdate,
  searchQuery,
  subUserData,
  todUrl,
  touUrl,
  ppUrl,
  canAddTakedown,
}) => {
  const { currentUser } = useContext(StateContext);

  const { ExportCSVButton } = CSVExport;
  
  const { t } = useTranslation();

  const { acr_deep_scan } = currentUser;

  const [successAction, setSuccessAction] = useState(false);
  const [declarationSignSuccess, setDeclarationSignSuccess] = useState(false);
  const [declarationSignError, setDeclarationSignError] = useState(false);
  const [preScanSuccess, setPreScanSuccess] = useState(false);
  const [preScanError, setPreScanError] = useState(false);
  const [preScanDeclarationSignSuccess, setPreScanDeclarationSignSuccess] = useState(false);
  const [preScanDeclarationSignError, setPreScanDeclarationSignError] = useState(false);

  const statusFormatter = cell => {
    return (
      <>
        {cell === "ready" ? (
          <Button className="btn-status-big" disabled color="info">
            {t("releases.view.table.label.status_ready")}
          </Button>
        ) : cell === "approval" ? (
          <Button className="btn-status-big" disabled color="warning">
            {t("releases.view.table.label.status_approval")}
          </Button>
        ) : cell === "takedown_request" ? (
          <Button className="btn-status-big" disabled color="warning">
            {t("releases.view.table.label.status_takedown_request")}
          </Button>
        ) : cell === "taken_down" ? (
          <Button className="btn-status-big" disabled color="danger">
            {t("releases.view.table.label.status_taken_down")}
          </Button>
        ) : cell === "distributed" ? (
          <Button className="btn-status-big" disabled color="scooter">
            {t("releases.view.table.label.status_distributed")}
          </Button>
        ) : cell === "locked" ? (
          <Button className="btn-status-big" disabled color="info">
            {t("releases.view.table.label.status_locked")}
          </Button>
        ) : cell === "delivering" ? (
          <Button className="btn-status-big" disabled color="info">
            {t("releases.view.table.label.status_delivering")}
          </Button>
        ) : cell === "unlocked" ? (
          <Button className="btn-status-big" disabled color="warning">
            {t("releases.view.table.label.status_unlocked")}
          </Button>
        ) : cell === "offline" ? (
          <Button className="btn-status-big" disabled color="danger">
            {t("releases.view.table.label.status_offline")}
          </Button>
        ) : cell === "re_delivery-delivery" ? (
          <Button className="btn-status-big" disabled color="warning">
            {t("releases.view.table.label.status_re_delivery-delivery")}
          </Button>
        ) : cell === "re_delivery-editing" ? (
          <Button className="btn-status-big" disabled color="warning">
            {t("releases.view.table.label.status_re_delivery-editing")}
          </Button>
        ) : (
          <Button className="btn-status" disabled>{t("releases.view.table.label.status_unknown")}</Button>
        )}
      </>
    );
  };

  const acrAlertFormatter = (cell, row) => {
    return (
      <ACRAlert {...row} action={{setDeclarationSignError, setDeclarationSignSuccess, forceUpdate, setForceUpdate}} />
    );
  };

  function rowHighlightStyle(row, rowIndex) {
    let hasQCFeedback = !row.qc_passed && !row.edited_after_qc && row.qc_feedback.results && Object.keys(row.qc_feedback.results).length !== 0;
    if (hasQCFeedback) {
      return "qc-issues-alert";
    }
    return "";
  }

  const nameFormatter = (cell, row) => {
    const SRC = row.artwork ? row.artwork.thumb_small : null;
    return (
      <div className="flexContainer">
        {SRC && <img style={{width: 40, height: 40, borderRadius: '100%'}} src={SRC} alt={cell}/> }
        <p className="fontWeight700">{cell}</p>
      </div>
    );
  };

  const artistFormatter = (cell, row) => {
    return (
      <p className="fontWeight700">{cell}</p>
    );
  };

  const dateFormatter = (cell, row) => {
    return (
      <div className="releaseDate">
        <p>{cell}: Official</p>
        {row.exclusive_date ? (
          <p>{row.exclusive_date}: Exclusive</p>
        ) : (
          <p>No exclusive date</p>
        )}
      </div>
    );
  };

  const actionFormatter = (cell, row) => {
    return (
      <ReleaseDropdown
        {...row}
        action={{
          successAction,
          setSuccessAction,
          forceUpdate,
          setForceUpdate
        }}
        todUrl={todUrl}
        touUrl={touUrl}
        ppUrl={ppUrl}
        canAddTakedown={canAddTakedown}
        preScanEnabled={acr_deep_scan}
      ></ReleaseDropdown>
    );
  };

  const storeUrlsFormatter = (cell, row) => {
    return (
      <ReleaseStoreUrls {...row}></ReleaseStoreUrls>
    );
  };

  const flaggedContentFormatter = (cell, row) => {
    return (
      <FlaggedContent {...row} action={{forceUpdate, setForceUpdate}} />
    );
  };

  const preScanFormatter = (cell, row) => {
    return (
      <PreScan {...row} action={{setPreScanSuccess, setPreScanError, setPreScanDeclarationSignSuccess, setPreScanDeclarationSignError, forceUpdate, setForceUpdate}} />
    );
  };

  const columns = [
    {
      dataField: "name",
      text: t("releases.view.table.column.name"),
      formatter: nameFormatter,
      sort: true,
    },
    {
      dataField: "name",
      hidden: true,
      csvExport: false
    },
    {
      dataField: "get_artists",
      text: t("releases.view.table.column.artist"),
      sort: false,
      formatter: artistFormatter
    },
    {
      dataField: "catalogue_number",
      text: t("releases.view.table.column.catalogue_number"),
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "175px" };
      }
    },
    {
      dataField: "official_date",
      text: t("releases.view.table.column.release_date"),
      sort: true,
      formatter: dateFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "180px" };
      }
    },
    {
      //date for search
      dataField: "official_date",
      hidden: true,
      csvExport: false,
    },
    {
      dataField: "status",
      text: t("releases.view.table.column.status"),
      sort: true,
      formatter: statusFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "160px" };
      }
    },
    {
      //action for search
      dataField: "status",
      hidden: true,
      csvExport: false
    },
    {
      dataField: "store_urls",
      text: t("releases.view.table.column.store_urls"),
      formatter: storeUrlsFormatter,
      sort: false,
      csvExport: false,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "qc",
      text: t("releases.view.table.column.qc"),
      formatter: qcNoteFormatter,
      sort: false,
      csvExport: false,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    ...(acr_deep_scan
      ? [{
        dataField: "pre_scan",
        text: t("releases.view.table.column.pre_scan"),
        formatter: preScanFormatter,
        sort: false,
        csvExport: false,
        headerStyle: (colum, colIndex) => {
          return { width: "80px" };
        }
      }]
      : []),
    {
      dataField: "ACR Alert",
      text: t("releases.view.table.column.alert"),
      formatter: acrAlertFormatter,
      sort: false,
      csvExport: false,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "has_flagged_tracks",
      text: t("releases.view.table.column.flagged"),
      formatter: flaggedContentFormatter,
      sort: false,
      csvExport: false,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "action",
      text: t("releases.view.table.column.action"),
      formatter: actionFormatter,
      sort: false,
      csvExport: false,
      headerStyle: (colum, colIndex) => {
        return { width: "111px" };
      }
    }
  ];

  const handleTableChange = (type, { sortField, sortOrder, data }) => {
    const ascDesc = sortOrder === "desc" ? "-" : "";
    if (sortField === "name" || sortField === "status" || sortField === "official_date" || sortField === "catalogue_number") {
      searchQuery.setOrdering({order: ascDesc, name: sortField, sort: sortField});
    }
  }

  return (
    <>
      <ViewLayout title={t("releases.view.table.title")}>
        <ToolkitProvider
          keyField="id"
          data={tableData}
          columns={columns}
          search={{
            searchFormatted: true
          }}
          exportCSV={ {
            fileName: 'release.csv',
          } }
        >
          {props => (
            <div>
              <Row>
                <Col>
                  <div className="customSearch">
                    <Input placeholder={t("releases.view.table.placeholder.search")} value={searchQuery.query} style={{marginBottom: 10}} onChange={e => searchQuery.setQuery(e.target.value)}/>
                  </div>
                </Col>
                {
                  currentUser.is_premium_user && <SubUserFilter subUserData={subUserData} />
                }
                <Col>
                  <div className="flexEndContainer">
                    <ExportCSVButton
                      className="btn btn-outline-info"
                      {...props.csvProps}
                    >
                      {t("releases.view.table.button.export_csv")}
                    </ExportCSVButton>
                  </div>
                </Col>
              </Row>

              <BootstrapTable
                rowClasses={rowHighlightStyle}
                {...props.baseProps}
                bordered={ false }
                remote={ { sort: true } }
                defaultSorted={defaultSorted}
                onTableChange={handleTableChange}
              />
            </div>
          )}
        </ToolkitProvider>
      </ViewLayout>
      {successAction && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          {t("releases.view.table.message.success")}
        </Alert>
      )}
      {declarationSignSuccess && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          {t("releases.view.table.message.declaration_success")}
        </Alert>
      )}
      {declarationSignError && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="error">
          {t("releases.view.table.message.declaration_error")}
        </Alert>
      )}
      {preScanDeclarationSignSuccess && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          {t("releases.view.table.message.pre_scan_declaration_success")}
        </Alert>
      )}
      {preScanDeclarationSignError && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="error">
          {t("releases.view.table.message.pre_scan_declaration_error")}
        </Alert>
      )}
      {preScanSuccess && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          {t("releases.view.table.message.pre_scan_success")}
        </Alert>
      )}
      {preScanError && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="error">
          {t("releases.view.table.message.pre_scan_error")}
        </Alert>
      )}
    </>
  );
};

export default ReleaseTable;
